import type * as CSS from 'csstype';
import styled from 'styled-components';

import { SmallParagraph } from '../../../../foundations/typefaces/SmallParagraph';
import style from '../../../../style';
import type { Color, Size } from '../../../../style/types/theme';
import { BubblePosition, Variant } from '../../types';

interface WrapperProps {
  $position: BubblePosition;
}

interface StyledBubbleProps {
  $show?: boolean;
  $position: BubblePosition;
  $containHtml?: boolean;
  $breakAll?: boolean;
  $variant?: Variant;
  $width?: string;
}

interface SpaceProps extends StyledBubbleProps {
  $height: string;
}

interface ArrowAligment {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

interface Transform {
  hidden: string;
  show: string;
}

const isHorizontalPosition = (position: BubblePosition): boolean =>
  position === BubblePosition.TOP || position === BubblePosition.BOTTOM;

const getTransform = (position: BubblePosition, size: Size): Transform => {
  const transforms = {
    [BubblePosition.TOP]: {
      hidden: `translateY(${size.units(0.5)})`,
      show: 'translateY(0)',
    },
    [BubblePosition.RIGHT]: {
      hidden: `translateX(${size.units(-0.5)})`,
      show: 'translateX(0)',
    },
    [BubblePosition.BOTTOM]: {
      hidden: `translateY(${size.units(-0.5)})`,
      show: 'translateY(0)',
    },
    [BubblePosition.LEFT]: {
      hidden: `translateX(${size.units(0.5)})`,
      show: 'translateY(0)',
    },
  };
  return transforms[position];
};

const getTextAlign = (position: BubblePosition): CSS.Property.TextAlign => {
  switch (position) {
    case BubblePosition.RIGHT:
      return 'left';
    case BubblePosition.LEFT:
      return 'right';
    default:
      return 'center';
  }
};

const getArrowAlignment = (position: BubblePosition, size: Size): ArrowAligment => {
  const alignment = {
    [BubblePosition.TOP]: {
      top: '100%',
      right: undefined,
      bottom: undefined,
      left: `calc(50% - ${size.units(0.5)})`,
    },
    [BubblePosition.RIGHT]: {
      top: `calc(50% - ${size.units(0.5)})`,
      right: '100%',
      bottom: undefined,
      left: undefined,
    },
    [BubblePosition.BOTTOM]: {
      top: undefined,
      right: undefined,
      bottom: '100%',
      left: `calc(50% - ${size.units(0.5)})`,
    },
    [BubblePosition.LEFT]: {
      top: `calc(50% - ${size.units(0.5)})`,
      right: undefined,
      bottom: undefined,
      left: '100%',
    },
  };

  return alignment[position];
};

const getDirection = (position: BubblePosition): CSS.Property.FlexDirection => {
  const positions: Record<BubblePosition, CSS.Property.FlexDirection> = {
    top: 'column',
    right: 'row-reverse',
    bottom: 'column-reverse',
    left: 'row',
  };

  return positions[position];
};

const getBorderColor = (
  doesMatchPosition: boolean,
  variant: Variant,
  color: Color,
): string => {
  if (!doesMatchPosition) {
    return 'transparent';
  }

  if (variant === Variant.LIGHT) {
    return color.border.reversed.default();
  }

  return color.border.tertiary.default();
};

const getBorderColorToArrow = (
  position: BubblePosition,
  variant: Variant,
  color: Color,
): CSS.Property.BorderColor => {
  const topColor = getBorderColor(position === BubblePosition.TOP, variant, color);
  const rightColor = getBorderColor(position === BubblePosition.RIGHT, variant, color);
  const bottomColor = getBorderColor(position === BubblePosition.BOTTOM, variant, color);
  const leftColor = getBorderColor(position === BubblePosition.LEFT, variant, color);

  return `${topColor} ${rightColor} ${bottomColor} ${leftColor}`;
};

const getBackgroundColor = (variant: Variant, color: Color) => {
  if (variant === Variant.LIGHT) {
    return color.background.primary.default();
  }

  return color.background.reversed.default();
};

const getColor = (variant: Variant, color: Color) => {
  if (variant === Variant.LIGHT) {
    return color.content.primary.default();
  }

  return color.content.reversed.default();
};

const getColorHover = (variant: Variant, color: Color) => {
  if (variant === Variant.LIGHT) {
    return color.content.primary.hover();
  }

  return color.content.reversed.hover();
};

export const Wrapper = styled.div<WrapperProps>(({ $position }) => ({
  display: 'flex',
  flexDirection: getDirection($position),
}));

export const StyledBubble = styled(SmallParagraph)<StyledBubbleProps>(({
  theme: { size, borderRadius, shadow, color, transition },
  $show = false,
  $position,
  $containHtml = false,
  $breakAll = false,
  $variant = Variant.DARK,
  $width,
}) => {
  const arrowAlignment = getArrowAlignment($position, size);
  const transform = getTransform($position, size);

  return {
    display: $containHtml ? undefined : 'flex',
    height: '100%',
    width: $width,
    maxWidth: size.units(30),
    borderRadius: borderRadius.small,
    boxShadow: shadow.short,
    backgroundColor: getBackgroundColor($variant, color),
    textAlign: getTextAlign($position),
    opacity: $show ? 1 : 0,
    userSelect: 'none',
    wordBreak: $breakAll ? 'break-word' : 'normal',
    whiteSpace: $breakAll ? 'pre-line' : undefined,
    marginBlock: 0,

    ...style.mixins.increaseSpecificity(
      {
        color: getColor($variant, color),
        padding: `${size.units(0.5)} ${size.units(1)}`,

        a: {
          cursor: 'pointer',
          textDecoration: 'underline',
          pointerEvents: $show ? 'auto' : 'none',

          ':hover': {
            color: getColorHover($variant, color),
            transition: transition.standard(),
          },
        },
      },
      3,
    ),

    transform: $show ? transform.show : transform.hidden,
    transition: $show
      ? 'transform .2s cubic-bezier(.4,0,.4,3),opacity .2s cubic-bezier(.4,0,.4,1)'
      : 'transform .2s cubic-bezier(.4,0,.4,1),opacity .2s cubic-bezier(.4,0,.4,1)',

    /* Tooltip Arrow */
    '&:after': {
      content: "''",
      position: 'absolute',
      display: 'block',
      top: arrowAlignment.top,
      right: arrowAlignment.right,
      bottom: arrowAlignment.bottom,
      left: arrowAlignment.left,
      borderWidth: size.units(0.5),
      borderStyle: 'solid',
      borderColor: getBorderColorToArrow($position, $variant, color),
    },
  };
});

export const Space = styled.div<SpaceProps>(
  ({ theme: { size }, $show = false, $height, $position }) => ({
    visibility: $show ? 'visible' : 'hidden',
    backgroundColor: 'transparent',
    width: isHorizontalPosition($position) ? undefined : size.units(1),
    height: isHorizontalPosition($position) ? size.units(1) : $height,
  }),
);
